import { resourcesApi, Resource } from '../services/resources-api';
import { ClassificationReviewPanel, ClassificationData } from './ClassificationReviewPanel';

export class ClassificationQueueView {
  private container: HTMLElement;
  private items: Resource[] = [];
  private selectedIndex: number = -1;
  private total: number = 0;
  private loading: boolean = false;

  constructor() {
    this.container = document.getElementById('classification-queue-view') as HTMLElement;
  }

  async load(): Promise<void> {
    if (this.loading) return;
    this.loading = true;

    try {
      // Fetch items pending review (humanReview is false/null)
      const result = await resourcesApi.list({
        humanReview: false,
        limit: 100,
        parentId: null // Only parent documents, not extracted elements
      });

      this.items = result.resources || [];
      this.total = result.total || this.items.length;
      this.selectedIndex = this.items.length > 0 ? 0 : -1;

      this.render();
    } catch (error) {
      console.error('Failed to load classification queue:', error);
      this.items = [];
      this.render();
    } finally {
      this.loading = false;
    }
  }

  private render(): void {
    if (!this.container) return;

    this.container.innerHTML = `
      <div class="classification-queue">
        <div class="classification-queue-header">
          <h2>Classification Review</h2>
          <span class="classification-queue-count">${this.total} pendientes</span>
        </div>

        <div class="classification-queue-content">
          <div class="classification-queue-list">
            ${this.items.length === 0 ? `
              <div class="classification-queue-empty">
                <span class="material-symbols-outlined">check_circle</span>
                <p>No hay items pendientes de revisión</p>
              </div>
            ` : this.items.map((item, index) => `
              <div class="classification-queue-item ${index === this.selectedIndex ? 'selected' : ''}" data-index="${index}">
                <div class="item-title">${this.escapeHtml(item.title || 'Sin título')}</div>
                <div class="item-meta">
                  <span class="item-category">${item.resourceCategory || 'Sin categoría'}</span>
                  <span class="item-date">${this.formatDate(item.createdAt)}</span>
                </div>
              </div>
            `).join('')}
          </div>

          <div class="classification-queue-panel">
            ${this.selectedIndex >= 0 ? `
              <div id="queue-review-panel"></div>
            ` : `
              <div class="classification-queue-empty">
                <span class="material-symbols-outlined">touch_app</span>
                <p>Selecciona un item para revisar</p>
              </div>
            `}
          </div>
        </div>
      </div>

      <style>
        .classification-queue {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: var(--bg-main);
        }
        .classification-queue-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid var(--border);
        }
        .classification-queue-header h2 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
        }
        .classification-queue-count {
          padding: 4px 10px;
          background: var(--primary);
          color: white;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
        }
        .classification-queue-content {
          display: flex;
          flex: 1;
          overflow: hidden;
        }
        .classification-queue-list {
          width: 300px;
          min-width: 250px;
          border-right: 1px solid var(--border);
          overflow-y: auto;
        }
        .classification-queue-item {
          padding: 12px 16px;
          border-bottom: 1px solid var(--border);
          cursor: pointer;
          transition: background 0.15s;
        }
        .classification-queue-item:hover {
          background: var(--bg-hover);
        }
        .classification-queue-item.selected {
          background: var(--bg-panel);
          border-left: 3px solid var(--primary);
        }
        .item-title {
          font-size: 13px;
          font-weight: 500;
          color: var(--text-main);
          margin-bottom: 4px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .item-meta {
          display: flex;
          gap: 8px;
          font-size: 11px;
          color: var(--text-muted);
        }
        .item-category {
          padding: 2px 6px;
          background: var(--bg-hover);
          border-radius: 3px;
        }
        .classification-queue-panel {
          flex: 1;
          padding: 20px;
          overflow-y: auto;
        }
        .classification-queue-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 200px;
          color: var(--text-muted);
          gap: 12px;
        }
        .classification-queue-empty .material-symbols-outlined {
          font-size: 48px;
          opacity: 0.5;
        }
        .classification-queue-empty p {
          margin: 0;
          font-size: 14px;
        }
      </style>
    `;

    this.bindEvents();

    // Render review panel for selected item
    if (this.selectedIndex >= 0) {
      this.renderReviewPanel();
    }
  }

  private bindEvents(): void {
    // Item selection
    this.container.querySelectorAll('.classification-queue-item').forEach(item => {
      item.addEventListener('click', () => {
        const index = parseInt(item.getAttribute('data-index') || '-1', 10);
        if (index >= 0) {
          this.selectItem(index);
        }
      });
    });

    // Keyboard navigation
    document.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  private handleKeydown = (e: KeyboardEvent): void => {
    // Only handle if this view is visible
    if (!this.container || this.container.classList.contains('hidden')) return;

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.selectItem(Math.max(0, this.selectedIndex - 1));
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.selectItem(Math.min(this.items.length - 1, this.selectedIndex + 1));
    }
  };

  private selectItem(index: number): void {
    if (index < 0 || index >= this.items.length) return;

    this.selectedIndex = index;

    // Update selection in list
    this.container.querySelectorAll('.classification-queue-item').forEach((item, i) => {
      item.classList.toggle('selected', i === index);
    });

    // Scroll selected item into view
    const selectedEl = this.container.querySelector('.classification-queue-item.selected');
    selectedEl?.scrollIntoView({ block: 'nearest' });

    // Render review panel
    this.renderReviewPanel();
  }

  private renderReviewPanel(): void {
    const panelContainer = this.container.querySelector('#queue-review-panel') as HTMLElement;
    if (!panelContainer || this.selectedIndex < 0) return;

    const item = this.items[this.selectedIndex];

    const classificationData: ClassificationData = {
      id: item.id,
      type: item.type,
      resourceCategory: item.resourceCategory,
      tags: item.tags,
      summary: (item.customFields?.summary as string) || '',
      contentFormat: (item.customFields?.contentFormat as string) || undefined,
      hookType: (item.customFields?.hookType as string) || undefined,
      confidence: (item.metadata?.confidence as number) || undefined,
      content: item.content || undefined,
      title: item.title
    };

    const reviewPanel = new ClassificationReviewPanel(panelContainer, classificationData, {
      showContentPreview: true,
      onAccept: () => {
        this.handleItemProcessed();
      },
      onSave: () => {
        this.handleItemProcessed();
      }
    });

    reviewPanel.render();
  }

  private handleItemProcessed(): void {
    // Remove processed item from list
    this.items.splice(this.selectedIndex, 1);
    this.total = Math.max(0, this.total - 1);

    // Adjust selection
    if (this.items.length === 0) {
      this.selectedIndex = -1;
    } else if (this.selectedIndex >= this.items.length) {
      this.selectedIndex = this.items.length - 1;
    }

    // Re-render
    this.render();
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private formatDate(timestamp: number): string {
    const date = new Date(timestamp);
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
  }

  // Clean up event listeners
  destroy(): void {
    document.removeEventListener('keydown', this.handleKeydown);
  }
}
